import {
  includesCompanionPhrase,
  normalizeCompanionTranscript
} from "./companionVoice";

export type VideoCompanionStage =
  | "p1_recommend"
  | "p2_playing"
  | "p3_continue"
  | "p4_quicklook"
  | "p5_actor";

export type VideoCompanionVideoId =
  | "world-cup-live"
  | "crossroads-continue"
  | "grandma-letter-hot"
  | "interstellar-trailer"
  | "dune-three-quick"
  | "dune-three-feature"
  | "wandering-city-short"
  | "dune-universe-recap";

export type VideoCompanionVoiceIntent = "actor" | "quicklook" | "continue" | "reset";

export interface VideoCompanionDemoState {
  active: true;
  selectedVideoId: VideoCompanionVideoId;
  stage: VideoCompanionStage;
}

export interface VideoCompanionVideo {
  id: VideoCompanionVideoId;
  title: string;
  subtitle: string;
  category: string;
  durationLabel: string;
  imageSrc: string;
  description: string;
  tags: string[];
  person: VideoCompanionPersonProfile;
  recognizedPeople?: VideoCompanionPersonProfile[];
}

export interface VideoCompanionPersonProfile {
  name: string;
  tabLabel?: string;
  avatarImageSrc?: string;
  avatarPosition?: string;
  roleLabel: string;
  relation: string;
  imageSrc?: string;
  summary: string;
  facts: string[];
  sourceLabel: string;
  sourceUrl: string;
  imageCredit?: string;
}

export interface VideoCompanionRecommendation {
  videoId: VideoCompanionVideoId;
  badge: string;
  note: string;
  linkLabel?: string;
}

export const VIDEO_COMPANION_CONTEXT = {
  time: "18:40",
  chargingCopy: "预计充电 32 分钟 · 够看一部短的",
  chargingStatus: "超充已连接 · 剩余 32 分钟",
  stationName: "星越超充站 B2",
  batteryLevel: "37%",
  targetLevel: "80%"
} as const;

const DUNE_TIMOTHEE_PROFILE: VideoCompanionPersonProfile = {
  name: "提莫西·查拉梅",
  tabLabel: "甜茶",
  avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-paul-still.webp",
  roleLabel: "演员",
  relation: "《沙丘》系列保罗·厄崔迪饰演者",
  imageSrc: "assets/figma/night-ai/video-companion/people/person-timothee-chalamet.jpg",
  summary: "提莫西·查拉梅是美国、法国演员，在《沙丘》系列中饰演保罗·厄崔迪。这一角色从贵族继承人逐步成为弗雷曼人的精神领袖，是理解影片权力与预言线的核心人物。",
  facts: ["代表作《沙丘》《请以你的名字呼唤我》", "饰演保罗·厄崔迪", "多次获得奥斯卡、BAFTA 等提名"],
  sourceLabel: "Wikipedia / Wikimedia Commons",
  sourceUrl: "https://en.wikipedia.org/wiki/Timoth%C3%A9e_Chalamet",
  imageCredit: "Amy Martin Photography / Wikimedia Commons · CC BY 4.0"
};

const DUNE_ZENDAYA_PROFILE: VideoCompanionPersonProfile = {
  name: "赞达亚",
  tabLabel: "赞达亚",
  avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-chani-still.webp",
  roleLabel: "演员 / 歌手",
  relation: "《沙丘》系列契妮饰演者",
  imageSrc: "assets/figma/night-ai/video-companion/people/person-zendaya.jpg",
  summary: "赞达亚在《沙丘》系列中饰演弗雷曼战士契妮。她既是保罗了解厄拉科斯与弗雷曼文化的重要向导，也代表着对权力、预言和外来统治保持警惕的现实视角。",
  facts: ["代表作《亢奋》《蜘蛛侠》系列", "饰演弗雷曼战士契妮", "艾美奖剧情类最佳女主角得主"],
  sourceLabel: "Wikipedia / Wikimedia Commons",
  sourceUrl: "https://en.wikipedia.org/wiki/Zendaya",
  imageCredit: "Glenn Francis · CC BY-SA 4.0"
};

const DUNE_REBECCA_PROFILE: VideoCompanionPersonProfile = {
  name: "丽贝卡·弗格森",
  tabLabel: "丽贝卡",
  avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-jessica-still.webp",
  roleLabel: "演员",
  relation: "《沙丘》系列杰西卡夫人饰演者",
  imageSrc: "assets/figma/night-ai/video-companion/people/person-rebecca-ferguson.jpg",
  summary: "丽贝卡·弗格森在《沙丘》系列中饰演杰西卡夫人。她兼具母亲、贝尼·杰瑟里特成员与政治行动者三重身份，推动了保罗命运线和弗雷曼宗教叙事的发展。",
  facts: ["代表作《碟中谍》《羊毛战记》", "饰演杰西卡夫人", "角色连接厄崔迪家族与贝尼·杰瑟里特"],
  sourceLabel: "Wikipedia / Wikimedia Commons",
  sourceUrl: "https://en.wikipedia.org/wiki/Rebecca_Ferguson",
  imageCredit: "Wikimedia Commons · CC BY-SA 4.0"
};

const DUNE_JAVIER_PROFILE: VideoCompanionPersonProfile = {
  name: "哈维尔·巴登",
  tabLabel: "哈维尔",
  avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-stilgar-still.webp",
  roleLabel: "演员",
  relation: "《沙丘》系列史迪尔格饰演者",
  imageSrc: "assets/figma/night-ai/video-companion/people/person-javier-bardem-photo.webp",
  summary: "哈维尔·巴登是西班牙演员，在《沙丘》系列中饰演弗雷曼领袖史迪尔格。他熟悉厄拉科斯的生存法则，并在保罗进入弗雷曼社会、获得信任与追随的过程中发挥关键作用。",
  facts: ["代表作《老无所依》《深海长眠》", "饰演弗雷曼领袖史迪尔格", "奥斯卡最佳男配角得主"],
  sourceLabel: "Wikimedia Commons",
  sourceUrl: "https://commons.wikimedia.org/wiki/File:Javier_Bardem_Cannes_2018.jpg",
  imageCredit: "Georges Biard · CC BY-SA 4.0"
};

const DUNE_RECOGNIZED_PEOPLE = [
  DUNE_REBECCA_PROFILE,
  DUNE_ZENDAYA_PROFILE,
  DUNE_JAVIER_PROFILE,
  DUNE_TIMOTHEE_PROFILE
];

const CROSSROADS_LUO_PROFILE: VideoCompanionPersonProfile = {
  name: "罗永浩",
  tabLabel: "罗永浩",
  avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-luo-yonghao-still.webp",
  roleLabel: "主持人 / 企业家",
  relation: "《罗永浩的十字路口》主持人",
  imageSrc: "assets/figma/night-ai/video-companion/people/person-luo-yonghao-online.webp",
  summary: "罗永浩曾任新东方英语教师，后来创办锤子科技，并进入直播电商和视频播客领域。在《罗永浩的十字路口》中，他通过长时间对谈讨论创业、产品与个人选择。",
  facts: ["《罗永浩的十字路口》主持人", "锤子科技创始人", "长期关注科技、产品与创业话题"],
  sourceLabel: "新浪新闻",
  sourceUrl: "https://k.sina.com.cn/article_1939716980_739dbb7404001231n.html",
  imageCredit: "新浪影像"
};

const CROSSROADS_TIM_PROFILE: VideoCompanionPersonProfile = {
  name: "潘天鸿",
  tabLabel: "TIM",
  avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-tim-still.webp",
  roleLabel: "影像创作者 / 企业家",
  relation: "影视飓风创始人、CEO",
  imageSrc: "assets/figma/night-ai/video-companion/people/person-tim-pan.webp",
  summary: "潘天鸿（Tim）是影视飓风创始人、CEO，也是一名影像创作者。他长期分享影视制作、摄影技术和器材知识，并带领团队持续探索高质量影像内容的制作方式。",
  facts: ["影视飓风创始人、CEO", "英国肯特大学电影系毕业", "长期创作影视、科普与数码内容"],
  sourceLabel: "数英网",
  sourceUrl: "https://www.digitaling.com/articles/766352.html",
  imageCredit: "数英网 / 影视飓风"
};

const CROSSROADS_RECOGNIZED_PEOPLE = [
  CROSSROADS_LUO_PROFILE,
  CROSSROADS_TIM_PROFILE
];

export const VIDEO_COMPANION_VIDEOS: VideoCompanionVideo[] = [
  {
    id: "world-cup-live",
    title: "世界杯直播·今晚焦点战",
    subtitle: "巴西 VS 日本 比分正焦灼",
    category: "体育直播",
    durationLabel: "直播中",
    imageSrc: "assets/figma/night-ai/video-companion/world-cup-brazil-japan-still.webp",
    description: "焦点战进入关键时段，适合充电等待时快速接入。",
    tags: ["世界杯", "直播", "焦点战"],
    person: {
      name: "马特乌斯·库尼亚",
      tabLabel: "库尼亚",
      avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-matheus-cunha-match-still.webp",
      roleLabel: "巴西足球运动员",
      relation: "巴西队 9 号前锋",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-matheus-cunha.webp",
      summary: "马特乌斯·库尼亚是巴西前锋，现效力曼联。在这场巴西对日本的比赛中，他身穿 9 号球衣，是画面中央与日本球员争抢的巴西队员。",
      facts: ["1999 年出生于巴西若昂佩索阿", "司职前锋 / 攻击型中场", "本场比赛身穿巴西队 9 号"],
      sourceLabel: "JFA / Wikimedia Commons",
      sourceUrl: "https://www.jfa.jp/eng/samuraiblue/worldcup_2026/final/match_page/m76.html",
      imageCredit: "Rodolfo Vilela / Wikimedia Commons · CC BY 3.0 BR"
    }
  },
  {
    id: "crossroads-continue",
    title: "罗永浩的十字路口·继续看",
    subtitle: "上次看到 35 分 20 秒",
    category: "访谈",
    durationLabel: "续播",
    imageSrc: "assets/figma/night-ai/video-companion/crossroads-two-person.webp",
    description: "延续上次观看进度，并沿用你的倍速偏好。",
    tags: ["访谈", "续播", "1.25x"],
    person: CROSSROADS_LUO_PROFILE,
    recognizedPeople: CROSSROADS_RECOGNIZED_PEOPLE
  },
  {
    id: "grandma-letter-hot",
    title: "给阿嬷的情书·热映中",
    subtitle: "剧情爱情 · 豆瓣 9.3",
    category: "电影",
    durationLabel: "热映",
    imageSrc: "assets/figma/night-ai/video-companion/grandma-letter-still.webp",
    description: "近期多人观看的温情电影，EVA 会保留充电提醒。",
    tags: ["热映", "剧情", "爱情"],
    person: {
      name: "李思潼",
      avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-li-sitong-still.webp",
      roleLabel: "演员",
      relation: "《给阿嬷的情书》核心主演",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-li-sitong-roadshow.webp",
      summary: "李思潼是《给阿嬷的情书》的核心主演之一，影片围绕亲情记忆、跨代沟通和潮汕地域情感展开。",
      facts: ["出演《给阿嬷的情书》", "影片入围并放映于多个华语电影相关活动", "角色线承担亲情叙事的主要情绪入口"],
      sourceLabel: "南方财经网 / 电影路演",
      sourceUrl: "https://www.sfccn.com/2026/5-6/5NMDE1MTNfMjEzOTM5NA.html",
      imageCredit: "广东财经大学 · 路演照片"
    }
  },
  {
    id: "interstellar-trailer",
    title: "星际旅途",
    subtitle: "太空科幻精选",
    category: "电影片段",
    durationLabel: "12 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/interstellar-trailer.webp",
    description: "节奏平稳的太空片段，适合充电等待时沉浸观看。",
    tags: ["科幻", "沉浸", "12 min"],
    person: {
      name: "马修·麦康纳",
      roleLabel: "演员",
      relation: "太空科幻片代表主演",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-matthew-mcconaughey.webp",
      summary: "马修·麦康纳是美国演员，曾主演《星际穿越》等作品；在太空科幻内容里常被联想到库珀这类关键角色。",
      facts: ["1969 年出生", "奥斯卡最佳男主角得主", "代表作包括《星际穿越》《达拉斯买家俱乐部》"],
      sourceLabel: "Wikipedia / Wikimedia Commons",
      sourceUrl: "https://en.wikipedia.org/wiki/Matthew_McConaughey",
      imageCredit: "Wikimedia Commons · CC BY-SA 2.0"
    }
  },
  {
    id: "dune-three-quick",
    title: "沙丘3",
    subtitle: "四分钟速看",
    category: "速看版",
    durationLabel: "4 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/dune-four-cast.webp",
    description: "快速回顾角色关系与关键冲突，充电结束前也能看完。",
    tags: ["新上线", "科幻", "4 min"],
    person: DUNE_TIMOTHEE_PROFILE,
    recognizedPeople: DUNE_RECOGNIZED_PEOPLE
  },
  {
    id: "dune-three-feature",
    title: "沙丘3",
    subtitle: "正片续看",
    category: "电影",
    durationLabel: "42 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/dune-four-cast.webp",
    description: "从当前角色线继续播放，EVA 保留充电倒计时提醒。",
    tags: ["正片", "续看", "42 min"],
    person: DUNE_TIMOTHEE_PROFILE,
    recognizedPeople: DUNE_RECOGNIZED_PEOPLE
  },
  {
    id: "wandering-city-short",
    title: "流浪地球",
    subtitle: "三分钟速看",
    category: "速看版",
    durationLabel: "3 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/wandering-earth-still.webp",
    description: "同类科幻短内容，剧情密度高，适合短等待。",
    tags: ["科幻", "城市", "3 min"],
    person: {
      name: "吴京",
      avatarImageSrc: "assets/figma/night-ai/video-companion/people/avatar-wu-jing-still.webp",
      roleLabel: "演员",
      relation: "《流浪地球》系列核心主演",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-wu-jing-portrait.webp",
      summary: "吴京是中国演员、导演，在《流浪地球》系列中出演关键角色；这类科幻速看内容会优先介绍他的角色动机和剧情作用。",
      facts: ["主演《流浪地球》系列", "代表作包括《战狼2》《长津湖》", "动作片和工业化科幻片的代表演员之一"],
      sourceLabel: "TMDB / Criticless",
      sourceUrl: "https://criticless.com/people/78871",
      imageCredit: "吴京人物照片"
    }
  },
  {
    id: "dune-universe-recap",
    title: "沙丘宇宙",
    subtitle: "人物关系回顾",
    category: "补课内容",
    durationLabel: "6 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/dune-universe-recap.webp",
    description: "把家族、星球和角色线重新串起来，看正片前更容易进入。",
    tags: ["人物", "设定", "6 min"],
    person: DUNE_TIMOTHEE_PROFILE,
    recognizedPeople: DUNE_RECOGNIZED_PEOPLE
  }
];

export const VIDEO_COMPANION_P1_RECOMMENDATIONS: VideoCompanionRecommendation[] = [
  { videoId: "world-cup-live", badge: "时效推荐", note: "你最近常看世界杯" },
  { videoId: "crossroads-continue", badge: "续播", note: "1.25 倍速沿用" },
  { videoId: "dune-three-quick", badge: "速看", note: "新上线" }
];

export const VIDEO_COMPANION_CONTINUE_RECOMMENDATIONS: VideoCompanionRecommendation[] = [
  { videoId: "grandma-letter-hot", badge: "片 1", note: "温情电影", linkLabel: "热映中" },
  { videoId: "wandering-city-short", badge: "片 2", note: "同类科幻短内容", linkLabel: "3分钟速看" }
];

export function createInitialVideoCompanionState(): VideoCompanionDemoState {
  return {
    active: true,
    selectedVideoId: "dune-three-quick",
    stage: "p1_recommend"
  };
}

export function getVideoCompanionVideo(videoId: VideoCompanionVideoId) {
  return VIDEO_COMPANION_VIDEOS.find((video) => video.id === videoId) ?? VIDEO_COMPANION_VIDEOS[0];
}

export function getVideoCompanionContinueRecommendations(videoId: VideoCompanionVideoId) {
  if (videoId === "grandma-letter-hot") {
    return [
      { videoId: "dune-three-quick", badge: "片 1", note: "科幻电影", linkLabel: "4分钟速看" },
      { videoId: "wandering-city-short", badge: "片 2", note: "同类科幻短内容", linkLabel: "3分钟速看" }
    ] satisfies VideoCompanionRecommendation[];
  }

  if (videoId === "wandering-city-short") {
    return [
      { videoId: "grandma-letter-hot", badge: "片 1", note: "温情电影", linkLabel: "热映中" },
      { videoId: "dune-three-quick", badge: "片 2", note: "科幻电影", linkLabel: "4分钟速看" }
    ] satisfies VideoCompanionRecommendation[];
  }

  return VIDEO_COMPANION_CONTINUE_RECOMMENDATIONS;
}

export function matchVideoCompanionVoiceIntent(transcript: string): VideoCompanionVoiceIntent | null {
  const normalizedTranscript = normalizeCompanionTranscript(transcript);

  if (!normalizedTranscript) {
    return null;
  }

  if (includesCompanionPhrase(normalizedTranscript, ["重新开始", "重置", "回到推荐", "回推荐页"])) {
    return "reset";
  }

  if (includesCompanionPhrase(normalizedTranscript, [
    "这人是谁",
    "这个人是谁",
    "这是谁",
    "他是谁",
    "她是谁",
    "演员是谁",
    "主演是谁",
    "主角是谁",
    "他叫什么",
    "她叫什么",
    "介绍一下这个人"
  ])) {
    return "actor";
  }

  if (includesCompanionPhrase(normalizedTranscript, ["片尾", "播完", "结束了", "看完了", "还有时间"])) {
    return "continue";
  }

  if (
    includesCompanionPhrase(normalizedTranscript, ["速看", "推荐", "看什么", "找一下", "帮我找", "类似"]) &&
    includesCompanionPhrase(normalizedTranscript, ["视频", "电影", "内容", "片", "短的", "短视频", "看"])
  ) {
    return "quicklook";
  }

  return null;
}
